// `GET /docs/audit/provenance` — PRD 16 C1, the provenance + audit trail.
// Server implementation is `backend/provenance/trail.py`, routed from
// `backend/api/routes/extract_audit.py` (the audit-trail router; `app.py` is
// frozen, so a new router could not be added for it).
//
// TWO STATES THIS FILE KEEPS APART, and collapsing them would be the bug:
//
//   401                -> signed out. NOT "this org has no record".
//   200 + docs: []     -> authenticated, and the org genuinely owns nothing.
//
// There is deliberately no third "partial" state. Incompleteness is not an
// error condition here — it is DATA, carried in `gaps`, and the screen
// renders it as prominently as the record itself.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getKey } from "../../dashboard/api";

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

/** One thing the record cannot answer. The server states these; it never
 *  omits a field it could not establish, and neither may this screen. */
export interface Gap {
  code: string;
  scope: "doc" | "corpus";
  subject: string;
  detail: string;
  affected: number;
}

export interface DocTrail {
  doc_id: string;
  title: string | null;
  corpus_id: string | null;
  pipeline_version: number | null;
  extraction_glossary_version: number | null;
  published_version: number | null;
  published_at: string | null;
  published_by: string | null;
  term_count: number;
  machine_term_count: number;
  accepted: number;
  edited: number;
  added: number;
  models: string[];
  gaps: Gap[];
}

export interface CorpusTrail {
  corpus_id: string;
  reconcile_run_count: number;
  first_run_at: string | null;
  last_run_at: string | null;
  decision_count: number;
  canonical_entity_count: number;
  unreviewed_canonical_count: number;
  gaps: Gap[];
}

export interface OrgTrail {
  org_slug: string;
  generated_at: string;
  docs: DocTrail[];
  corpora: CorpusTrail[];
  gaps: Gap[];
}

export class ProvenanceError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`GET /docs/audit/provenance responded ${status}`);
    this.name = "ProvenanceError";
    this.status = status;
  }
}

function authHeaders(): Record<string, string> {
  const key = getKey();
  return key
    ? { Accept: "application/json", "X-mAiSight-Key": key }
    : { Accept: "application/json" };
}

export async function fetchOrgTrail(): Promise<OrgTrail> {
  const res = await fetch(`${API_BASE}/docs/audit/provenance`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new ProvenanceError(res.status);
  return (await res.json()) as OrgTrail;
}

export function useOrgTrail(): UseQueryResult<OrgTrail, ProvenanceError> {
  return useQuery<OrgTrail, ProvenanceError>({
    queryKey: ["provenance-trail"],
    queryFn: fetchOrgTrail,
    retry: (count, error) => error.status !== 401 && count < 2,
  });
}

/** Every gap in the payload, doc and corpus alike, in one list.
 *
 *  Guards read the ARRAYS, never the payload object — the response is an
 *  `as` cast over untyped JSON, so `data` being truthy proves nothing about
 *  the shape underneath it. */
export function allGaps(trail: OrgTrail | undefined): Gap[] {
  if (!trail) return [];
  return [
    ...(Array.isArray(trail.gaps) ? trail.gaps : []),
    ...(Array.isArray(trail.docs) ? trail.docs : []).flatMap((d) =>
      Array.isArray(d.gaps) ? d.gaps : [],
    ),
    ...(Array.isArray(trail.corpora) ? trail.corpora : []).flatMap((c) =>
      Array.isArray(c.gaps) ? c.gaps : [],
    ),
  ];
}

/** Distinct gap codes, each with the number of rows it implicates.
 *  A gap with `affected: 0` is not countable, so it contributes its
 *  occurrence rather than a row count — never a silent zero. */
export function gapSummary(
  gaps: Gap[],
): { code: string; occurrences: number; affected: number }[] {
  const by = new Map<string, { occurrences: number; affected: number }>();
  for (const gap of gaps) {
    const seen = by.get(gap.code) ?? { occurrences: 0, affected: 0 };
    by.set(gap.code, {
      occurrences: seen.occurrences + 1,
      affected: seen.affected + (gap.affected || 0),
    });
  }
  return [...by.entries()]
    .map(([code, counts]) => ({ code, ...counts }))
    .sort((a, b) => b.affected - a.affected || a.code.localeCompare(b.code));
}
